import posthog, {
  type CaptureOptions,
  KnownEventName,
  KnownUnsafeEditableEvent,
} from "posthog-js"
import { isProductionEnv } from "@/common/constants/env"
import { isHostWhiteLabel } from "@/whitelabel/hooks/useIsWhiteLabel"

enum AnalyticsEvent {
  Stake = "stake",
  Unstake = "unstake",
  Delegate = "delegate",
  UpsellStakingCTA = "upsell_staking_cta",
  ProposalCreated = "proposal_created",
}

enum Properties {
  OrganizationId = "organization_id",
  OrganizationName = "organization_name",
  OrganizationSlug = "organization_slug",
  IsWhiteLabel = "is_white_label",
  ConnectorName = "connector_name",
  ConnectorId = "connector_id",
  ChainId = "chain_id",
  ChainName = "chain_name",
  ChainSlug = "chain_slug",
  ChainIsTestnet = "chain_is_testnet",
  ProposalId = "proposal_id",
  TxHash = "transaction_hash",
  Amount = "amount",
  UserAddress = "user_address",
  UserId = "user_id",
  UserName = "user_name",
}

type PropertyValue = string | number | boolean | null | undefined

/**
 * Analytics service that provides a layer of abstraction over PostHog
 * This allows for easy switching to different analytics providers in the future
 */
export class Analytics {
  /**
   * Available analytics events
   * Usage: AnalyticsService.events.OrganizationLoaded
   */
  static events = AnalyticsEvent

  /**
   * Available analytics registrations
   * Usage: AnalyticsService.registrations.Organization
   */
  static registrations = Properties

  /**
   * Higher-order function that wraps a function to execute conditionally
   * @param fn - The function to wrap
   * @returns A new function with the same signature that executes conditionally
   */
  private static conditionalExecution = <T extends (...args: any[]) => any>(
    fn: T,
  ): ((...args: Parameters<T>) => ReturnType<T> | undefined) => {
    return (...args: Parameters<T>) => {
      if (Analytics.isEnabled()) {
        return fn(...args)
      }
      return undefined
    }
  }

  /**
   * Get global state
   * @returns Object containing global state like isWhiteLabel
   */
  private static withContext = (
    object: Partial<Record<Properties, PropertyValue>> | null = {},
  ) => {
    return {
      ...(typeof window !== "undefined" && {
        isWhiteLabel: isHostWhiteLabel(window?.location?.host),
      }),
      ...object,
    }
  }

  static isEnabled = () => {
    return isProductionEnv && Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY)
  }

  static initialize = Analytics.conditionalExecution(() => {
    if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) {
      console.info(
        "NEXT_PUBLIC_POSTHOG_KEY is not set, analytics will not be initialized",
      )
      return
    }

    return posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
      api_host:
        process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com",
      person_profiles: "identified_only",
      capture_pageview: true,
      capture_pageleave: true,
      enable_heatmaps: true,
      autocapture: true,
      debug: false,
    })
  })

  static register = Analytics.conditionalExecution(
    (properties: Partial<Record<Properties, PropertyValue>>) => {
      return posthog.register(properties)
    },
  )

  /**
   * Track an event with properties and capture options
   * @param event - The event to track
   * @param properties - The properties to track with the event
   * @param captureOptions - Additional capture options
   * @returns The capture result from the analytics provider
   */
  static track = Analytics.conditionalExecution(
    (
      event: KnownUnsafeEditableEvent | KnownEventName | AnalyticsEvent,
      properties?: Partial<Record<Properties, PropertyValue>>,
      captureOptions?: CaptureOptions,
    ) => {
      return posthog.capture(
        event,
        Analytics.withContext(properties),
        captureOptions,
      )
    },
  )

  static identify = Analytics.conditionalExecution(
    (
      id: string,
      properties?: Partial<Record<Properties, PropertyValue>>,
      userPropertiesToSetOnce?: Partial<Record<Properties, PropertyValue>>,
    ) => {
      const isIdentified = posthog?._isIdentified()
      // If the user is already identified, we need to set the properties again
      // to make sure they are up to date
      if (isIdentified && properties) {
        return posthog.setPersonProperties(properties)
      }
      return posthog.identify(
        id,
        Analytics.withContext(properties),
        userPropertiesToSetOnce,
      )
    },
  )

  static reset = Analytics.conditionalExecution(
    (resetDeviceId: boolean = false) => {
      return posthog.reset(resetDeviceId)
    },
  )
}
